import React, { useState } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Icon from 'components/AppIcon';

const BookingPanel = ({ 
  selectedActivity, 
  isVisible, 
  onClose, 
  onBookingConfirm 
}) => {
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    participants: 1,
    specialRequests: '',
    contactName: '',
    contactPhone: '',
    contactEmail: ''
  });

  const [errors, setErrors] = useState({});

  const timeSlots = [
    { value: '06:00', label: '6:00 AM - Early Morning' },
    { value: '09:00', label: '9:00 AM - Morning' },
    { value: '14:00', label: '2:00 PM - Afternoon' },
    { value: '16:00', label: '4:00 PM - Evening' }
  ];

  const participantOptions = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} ${i === 0 ? 'Person' : 'People'}`
  }));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const calculateTotal = () => {
    if (!selectedActivity) return 0;
    const basePrice = selectedActivity?.price * bookingData?.participants;
    const tax = basePrice * 0.18; // 18% GST
    return basePrice + tax;
  };

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!bookingData?.date) newErrors.date = 'Please select a date';
    if (!bookingData?.time) newErrors.time = 'Please select a time slot';
    if (!bookingData?.contactName) newErrors.contactName = 'Name is required';
    if (!bookingData?.contactPhone) newErrors.contactPhone = 'Phone number is required';
    if (!bookingData?.contactEmail) newErrors.contactEmail = 'Email is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleBooking = () => {
    if (validateForm()) {
      const bookingDetails = {
        activity: selectedActivity,
        ...bookingData,
        total: calculateTotal(),
        bookingId: `ACT${Date.now()}`,
        status: 'pending'
      };
      onBookingConfirm(bookingDetails);
    }
  };

  if (!isVisible || !selectedActivity) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-organic-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-heading font-semibold text-card-foreground">
              Book Activity
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedActivity?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:text-card-foreground hover:bg-muted transition-micro"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Activity Summary */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Mountain" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-card-foreground">{selectedActivity?.name}</h3>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{selectedActivity?.duration}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Users" size={14} />
                    <span>{selectedActivity?.groupSize}</span>
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    selectedActivity?.difficulty === 'Easy' ? 'bg-success/10 text-success' :
                    selectedActivity?.difficulty === 'Moderate'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                  }`}>
                    {selectedActivity?.difficulty}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-primary">
                  {formatCurrency(selectedActivity?.price)}
                </div>
                <div className="text-xs text-muted-foreground">per person</div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date Selection */}
            <Input
              type="date"
              label="Preferred Date"
              value={bookingData?.date}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              error={errors?.date}
              min={new Date()?.toISOString()?.split('T')?.[0]}
              required
            />

            {/* Time Slot */}
            <Select
              label="Time Slot"
              options={timeSlots}
              value={bookingData?.time}
              onChange={(value) => handleInputChange('time', value)}
              error={errors?.time}
              placeholder="Select time slot"
              required
            />

            {/* Number of Participants */}
            <Select
              label="Number of Participants"
              options={participantOptions}
              value={bookingData?.participants}
              onChange={(value) => handleInputChange('participants', parseInt(value))}
              required
            />

            {/* Contact Name */}
            <Input
              type="text"
              label="Contact Name"
              placeholder="Enter your full name"
              value={bookingData?.contactName}
              onChange={(e) => handleInputChange('contactName', e?.target?.value)}
              error={errors?.contactName}
              required
            />

            {/* Contact Phone */}
            <Input
              type="tel"
              label="Phone Number"
              placeholder="+91 98765 43210"
              value={bookingData?.contactPhone}
              onChange={(e) => handleInputChange('contactPhone', e?.target?.value)}
              error={errors?.contactPhone}
              required
            />

            {/* Contact Email */}
            <Input
              type="email"
              label="Email Address"
              placeholder="your.email@example.com"
              value={bookingData?.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e?.target?.value)}
              error={errors?.contactEmail}
              required
            />
          </div>

          {/* Special Requests */}
          <div>
            <Input
              type="text"
              label="Special Requests (Optional)"
              placeholder="Any dietary restrictions, accessibility needs, or special occasions..."
              value={bookingData?.specialRequests}
              onChange={(e) => handleInputChange('specialRequests', e?.target?.value)}
            />
          </div>

          {/* Pricing Breakdown */}
          <div className="bg-muted rounded-lg p-4">
            <h3 className="font-medium text-card-foreground mb-3">Booking Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {selectedActivity?.name} × {bookingData?.participants}
                </span>
                <span className="font-mono">
                  {formatCurrency(selectedActivity?.price * bookingData?.participants)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Taxes & Fees (18%)</span>
                <span className="font-mono">
                  {formatCurrency(selectedActivity?.price * bookingData?.participants * 0.18)}
                </span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-semibold">
                <span>Total Amount</span>
                <span className="font-mono text-primary text-lg">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>
            </div>
          </div>

          {/* Safety Notice */}
          {selectedActivity?.safetyRequirements && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-warning mb-2">Safety Requirements</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {selectedActivity?.safetyRequirements?.map((req, index) => (
                      <li key={index}>• {req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleBooking}
              iconName="CreditCard"
              iconPosition="left"
              className="flex-1"
            >
              Confirm Booking
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPanel;